import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, ShieldCheck, Building2, Hammer, Ruler, Tag } from "lucide-react";
import { api } from "../lib/api";
import { AutumnOfferBanner } from "../components/AutumnOfferBanner";

export default function Home() {
  const [promo, setPromo] = useState(null);
  const [offers, setOffers] = useState(null);
  const [resume, setResume] = useState(null);
  useEffect(() => {
    api.get("/public/settings").then(r => {
      if (r.data?.promo_active) setPromo(r.data);
      if (r.data?.free_doors_active || r.data?.site_visit_mode) setOffers(r.data);
    }).catch(() => {});
    try {
      const raw = localStorage.getItem("de_resume");
      if (raw) {
        const r = JSON.parse(raw);
        if (r && r.id && (Date.now() - (r.ts || 0)) < 33 * 864e5) setResume(r);
        else localStorage.removeItem("de_resume");
      }
    } catch (e) { console.debug("Resume banner check failed:", e); }
  }, []);
  return (
    <main data-testid="home-page">
      {offers && <AutumnOfferBanner offers={offers} />}
      {promo && (
        <Link to="/quote" data-testid="home-promo-banner" className="block bg-[#C79A3B] text-[#12263A]">
          <div className="px-6 md:px-12 lg:px-16 py-3 flex items-center justify-center gap-3 text-sm md:text-base font-bold text-center">
            <Tag size={18} className="shrink-0"/>
            <span>Summer offer — pay your deposit today and get {Math.round((promo.launch_offer_pct || 0.1) * 100)}% OFF your build{promo.promo_end_date ? ` · ends ${new Date(promo.promo_end_date).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}` : ""}</span>
            <ArrowRight size={16} className="shrink-0"/>
          </div>
        </Link>
      )}
      {resume && (
        <div data-testid="home-resume-banner" className="bg-[#12263A] text-white">
          <div className="px-6 md:px-12 lg:px-16 py-3 flex items-center justify-center gap-4 text-sm text-center flex-wrap">
            <span className="font-semibold">Welcome back — your saved design{resume.total ? ` (£${Math.round(resume.total).toLocaleString("en-GB")})` : ""} is ready to book.</span>
            <Link to={`/quote?d=${resume.id}`} data-testid="home-resume-btn" className="inline-flex items-center gap-1.5 bg-[#C79A3B] hover:bg-[#A87E28] transition-colors px-4 py-1.5 rounded-full font-bold text-xs uppercase tracking-wider text-[#12263A]">Resume &amp; book <ArrowRight size={14}/></Link>
            <button data-testid="home-resume-dismiss" onClick={() => { localStorage.removeItem("de_resume"); setResume(null); }} className="text-white/50 hover:text-white" aria-label="Dismiss"><span className="text-lg leading-none">×</span></button>
          </div>
        </div>
      )}
      {/* HERO */}
      <section className="px-6 md:px-12 lg:px-16 pt-10 sm:pt-14 md:pt-20 lg:pt-24 pb-16 sm:pb-20 md:pb-28">
        <div className="max-w-4xl mx-auto text-center flex flex-col items-center">
          <img src="/brand/primary-main-logo.png" alt="The Extension Works — making home extensions simple" data-testid="hero-logo" className="hidden sm:block w-full max-w-[620px] h-auto mb-10 rounded-2xl" />
          <Link to="/quote" data-testid="home-from-price-banner" className="group inline-flex items-center gap-2 mb-7 rounded-full bg-[#C79A3B] text-[#12263A] px-5 py-2.5 text-xs sm:text-sm font-extrabold uppercase tracking-wider shadow-sm hover:bg-[#b6892f] transition-colors">
            <Tag size={16} className="shrink-0" /> Free instant online quote
            <ArrowRight size={14} className="shrink-0 transition-transform group-hover:translate-x-0.5" />
          </Link>
          <div className="label-eyebrow mb-6" data-testid="hero-eyebrow">Rear extensions · London, Surrey, Hampshire &amp; Berkshire · Permitted Development &amp; Planning</div>
          <h1 className="font-black tracking-tighter leading-[0.95] text-4xl sm:text-5xl lg:text-6xl" data-testid="hero-headline">
            One of the UK's first instant-quote, <span className="text-[#C79A3B]">online-booking</span> extension sites.
          </h1>
          <p className="mt-8 text-base md:text-lg text-[#6B7280] max-w-2xl leading-relaxed" data-testid="hero-subtext">
            Design, structure, building control and the build itself — one team, one fixed VAT-inclusive price.
            Configure your single-storey rear extension, watch it modelled live in 3D, get a real quote in minutes,
            then book your start date online. No sales visits. No waiting weeks for a number.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/quote" data-testid="hero-cta-quote" className="de-btn-primary">Get instant quote <ArrowRight size={16}/></Link>
            <Link to="/how-it-works" data-testid="hero-cta-how" className="de-btn-secondary">How it works</Link>
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="border-y border-[#E5E7EB] bg-white">
        <div className="px-6 md:px-12 lg:px-16 py-12 grid grid-cols-2 md:grid-cols-4 gap-y-10 gap-x-6">
          {[
            { k: "12-month", v: "workmanship warranty", I: ShieldCheck },
            { k: "1 team", v: "design · structure · build · BC", I: Building2 },
            { k: "VAT-incl.", v: "every price you see", I: Hammer },
            { k: "Live 3D", v: "your build, modelled in real time", I: Ruler },
          ].map((t) => (
            <div key={t.k} className="flex items-start gap-4">
              <t.I size={22} className="text-[#C79A3B] mt-1"/>
              <div>
                <div className="font-bold text-xl tracking-tight">{t.k}</div>
                <div className="text-xs text-[#6B7280] uppercase tracking-wider">{t.v}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* WHY / FEATURES */}
      <section className="bg-[#F4F4F4] border-y border-[#E5E7EB]" data-testid="home-features-section">
        <div className="px-6 md:px-12 lg:px-16 py-24 md:py-28">
          <div className="label-eyebrow mb-6">Why The Extension Works</div>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight max-w-3xl">Everything handled, from first sketch to final sign-off.</h2>
          <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              ["expert-design.png", "In-house architects turn your brief into buildable, regulation-ready plans."],
              ["clear-planning.png", "We confirm your Permitted Development or planning route and handle approvals for you."],
              ["quality-build.png", "One crew, one standard, backed by a 12-month workmanship warranty."],
              ["built-around-you.png", "Your spec, your start date — one fixed, VAT-inclusive price with no surprises."],
            ].map(([img, d]) => (
              <div key={img} className="de-card p-8 flex flex-col items-start" data-testid={`feature-${img.replace('.png','')}`}>
                <img src={`/brand/${img}`} alt={img.replace('.png','').replace('-',' ')} className="h-12 w-auto object-contain mb-6" loading="lazy" />
                <div className="text-sm text-[#6B7280] leading-relaxed">{d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* IMAGE BLOCK */}
      <section className="px-6 md:px-12 lg:px-16 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="aspect-[4/5] overflow-hidden">
            <img src="https://static.prod-images.emergentagent.com/jobs/08e6467f-45d5-40c2-bf49-a71b6550c6c8/images/35d9ddf5c6ebef37ea6ad280adef2a565a96ca7051b024775071fc2374bf0a9d.png" alt="Modern single-storey brick rear extension with bifold doors and garden" className="w-full h-full object-cover"/>
          </div>
          <div className="lg:pl-12">
            <div className="label-eyebrow mb-6">Why The Extension Works</div>
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight">One firm. Architects, project managers and builders — all in-house.</h2>
            <p className="mt-8 text-[#6B7280] leading-relaxed">
              We project-manage every job ourselves, with our own architects drawing the plans
              and our own crew on site. Building Control is organised on your behalf.
              You get one fixed price, one team, one point of contact, and one warranty
              covering the entire build.
            </p>
            <Link to="/about" className="inline-flex items-center gap-2 mt-8 de-link font-bold uppercase text-sm tracking-widest">Our story <ArrowRight size={14}/></Link>
          </div>
        </div>
      </section>

      {/* PERMITTED DEVELOPMENT AT A GLANCE */}
      <section className="border-y border-[#E5E7EB] bg-white" data-testid="home-pd-section">
        <div className="px-6 md:px-12 lg:px-16 py-24 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="label-eyebrow mb-6">Planning made simple</div>
              <h2 className="text-3xl md:text-5xl font-bold tracking-tight">Most extensions don't need full planning permission.</h2>
              <p className="mt-8 text-[#6B7280] leading-relaxed">
                Single-storey rear extensions usually fall under <b className="text-[#12263A]">Permitted Development</b> — up to
                <b className="text-[#12263A]"> 4m</b> deep on a detached house, or <b className="text-[#12263A]">3m</b> on a semi or terrace —
                with no planning application at all.
              </p>
              <p className="mt-4 text-[#6B7280] leading-relaxed">
                Want to go further? The <b className="text-[#12263A]">Larger Home Extension</b> scheme lets you build up to
                <b className="text-[#12263A]"> 6m</b> (semi/terraced) or <b className="text-[#12263A]">8m</b> (detached) deep with a simple
                <b className="text-[#12263A]"> prior-approval</b> application — still without full planning. We handle it for you.
              </p>
              <Link to="/extension-rules" data-testid="home-pd-link" className="inline-flex items-center gap-2 mt-8 de-link font-bold uppercase text-sm tracking-widest">
                See the full rules &amp; diagrams <ArrowRight size={14}/>
              </Link>
            </div>
            <div className="aspect-[3/2] overflow-hidden border border-[#E5E7EB]">
              <img src="https://static.prod-images.emergentagent.com/jobs/3e047fd2-aa5b-47d3-8929-6e98c2365ce5/images/8e1d554f8994dcc31bf6155b9b25f8847560219c6f798c7b5bb155be0bc16450.png" alt="Deep single-storey rear extension built under the larger home extension prior-approval scheme" loading="lazy" className="w-full h-full object-cover"/>
            </div>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="bg-[#F4F4F4] border-y border-[#E5E7EB]">
        <div className="px-6 md:px-12 lg:px-16 py-24 md:py-32">
          <div className="label-eyebrow mb-6">The process</div>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight max-w-3xl">Self-quote, book online, we build.</h2>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              ["01","Postcode & route","Quick check tells us if you qualify for Permitted Development — or if you already hold planning."],
              ["02","Live 3D quote","Parametric configurator. Width, depth, roof, doors. See it. Price it. Done."],
              ["03","Book a start","Calendar of available start weeks. Pay a 10% deposit to lock the slot."],
              ["04","From footings to finish","Architectural pack issued. Building control booked. Twelve-month warranty on completion."],
            ].map(([n,t,d]) => (
              <div key={n} className="de-card p-8">
                <div className="text-3xl font-black text-[#C79A3B]">{n}</div>
                <div className="mt-4 font-bold text-lg">{t}</div>
                <div className="mt-3 text-sm text-[#6B7280] leading-relaxed">{d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 md:px-12 lg:px-16 py-24 md:py-32">
        <div className="bg-[#12263A] text-white p-12 md:p-20 flex flex-col md:flex-row items-start md:items-center justify-between gap-10">
          <div>
            <div className="label-eyebrow mb-4 text-[#C79A3B]">Ready when you are</div>
            <h3 className="text-3xl md:text-5xl font-bold tracking-tight max-w-xl">Get a real price in under ten minutes.</h3>
          </div>
          <Link to="/quote" className="de-btn-primary">Start your quote <ArrowRight size={16}/></Link>
        </div>
      </section>
    </main>
  );
}
